import { NO_CATEGORY_FILTER } from '@klopotun/contracts';
import { toCivilDate } from '@klopotun/domain';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useCategoriesQuery } from '../../entities/category/index.js';
import { useMeQuery } from '../../entities/me/index.js';
import {
  type CategorySpending,
  type MerchantSpending,
  type PaymentSpending,
  useSummaryQuery,
} from '../../entities/summary/index.js';
import {
  operationDetailPath,
  paymentDetailPath,
  summaryMerchantPath,
} from '../../features/navigation/index.js';
import {
  byLargestSpending,
  categoryTitleOf,
  formatBaseMoney,
  formatMoneyMaybeApproximate,
  formatShare,
  LazySpendingChart,
  type SpendingEntry,
  spentMinorOf,
  toChosenPeriod,
  toSpendingBreakdown,
  useChosenPeriod,
} from '../../features/summary-view/index.js';
import { useBackButton } from '../../shared/telegram/index.js';
import { List, ListItem, PageHeader, Screen } from '../../shared/ui/index.js';
import { OperationList } from '../../widgets/bank-operations/index.js';
import {
  isSpendingApproximate,
  PeriodControls,
  SpendingMonths,
  SummaryFailureScreen,
  SummaryLoadingScreen,
  SummaryNotes,
  WithoutForecast,
  YearFigures,
} from '../../widgets/year-summary/index.js';
import { CategoryMerchants } from './category-merchants.js';
import styles from './summary-category-page.module.css';

const UNKNOWN_CATEGORY_TEXT = 'Категорія';

const BANK_SLICE_KEY = 'bank';

const BANK_SLICE_LABEL = 'Операції з картки';

const shareOf = (spentMinor: number, totalMinor: number): string | undefined => {
  const hasShare = spentMinor > 0 && totalMinor > 0;

  return hasShare ? formatShare(spentMinor / totalMinor) : undefined;
};

const isRowOf =
  (category: string) =>
  (row: CategorySpending): boolean =>
    (row.categoryId ?? NO_CATEGORY_FILTER) === category;

const isMerchantOf =
  (category: string) =>
  (merchant: MerchantSpending): boolean =>
    (merchant.categoryId ?? NO_CATEGORY_FILTER) === category;

const byLargestMerchant = (left: MerchantSpending, right: MerchantSpending): number =>
  right.spentMinor - left.spentMinor;

const toPaymentEntry = (row: PaymentSpending): SpendingEntry => ({
  key: row.paymentId,
  label: row.name,
  color: null,
  exactMinor: row.exactMinor,
  estimatedMinor: row.estimatedMinor,
});

const toBankEntry = (bankMinor: number): SpendingEntry => ({
  key: BANK_SLICE_KEY,
  label: BANK_SLICE_LABEL,
  color: null,
  exactMinor: bankMinor,
  estimatedMinor: 0,
});

const emptyPaymentsTextOf = (month: number | null): string =>
  month === null ? 'За цей рік витрат немає' : 'За цей місяць витрат немає';

export const SummaryCategoryPage = () => {
  const { category = NO_CATEGORY_FILTER } = useParams();
  const navigate = useNavigate();
  const { chosen, choose } = useChosenPeriod();
  const me = useMeQuery();
  const summary = useSummaryQuery(chosen.year, chosen.month, category);
  const categories = useCategoriesQuery({ includeArchived: true });
  const goBack = useCallback(() => {
    void navigate(-1);
  }, [navigate]);

  useBackButton(goBack);

  if (me.isPending || summary.isPending) {
    return <SummaryLoadingScreen />;
  }

  if (me.isError) {
    return (
      <SummaryFailureScreen
        message={me.error.message}
        onRetry={() => {
          void me.refetch();
        }}
      />
    );
  }

  if (summary.isError) {
    return (
      <SummaryFailureScreen
        message={summary.error.message}
        onRetry={() => {
          void summary.refetch();
        }}
      />
    );
  }

  const loaded = summary.data;
  const currency = loaded.baseCurrency;
  const isUncategorised = category === NO_CATEGORY_FILTER;
  const record = (categories.data ?? []).find((candidate) => candidate.id === category);
  const title =
    isUncategorised || record !== undefined ? categoryTitleOf(record) : UNKNOWN_CATEGORY_TEXT;
  const today = toCivilDate(new Date(), me.data.settings.timezone);
  const hasBank = loaded.bank !== null;
  const bankMinor = loaded.categories.find(isRowOf(category))?.bankMinor ?? 0;
  const breakdown = toSpendingBreakdown([
    ...loaded.payments.map(toPaymentEntry),
    toBankEntry(bankMinor),
  ]);
  const paymentRows = loaded.payments.toSorted(byLargestSpending);
  const merchants = loaded.merchants.filter(isMerchantOf(category)).toSorted(byLargestMerchant);
  const showsPayments = !hasBank || paymentRows.length > 0;
  const hasBankPart = bankMinor !== 0 || merchants.length > 0;
  const showsMerchants = hasBank && hasBankPart;

  return (
    <Screen>
      <div className={styles.content}>
        <PageHeader title={title} />
        <PeriodControls
          shown={{ year: loaded.year, month: loaded.month }}
          today={today}
          onChoose={choose}
        />

        <YearFigures summary={loaded} />
        <SummaryNotes summary={loaded} />

        {breakdown.slices.length > 0 && (
          <LazySpendingChart
            slices={breakdown.slices}
            totalMinor={breakdown.totalMinor}
            currency={currency}
            isApproximate={isSpendingApproximate(loaded)}
            label="Витрати по підписках і операціях з картки"
          />
        )}

        {showsPayments && (
          <List header="По підписках">
            {paymentRows.length === 0 && <ListItem title={emptyPaymentsTextOf(loaded.month)} />}
            {paymentRows.map((row) => (
              <ListItem
                key={row.paymentId}
                title={row.name}
                marker={breakdown.colorByKey.get(row.paymentId)}
                subtitle={shareOf(spentMinorOf(row), breakdown.totalMinor)}
                value={formatMoneyMaybeApproximate(
                  spentMinorOf(row),
                  currency,
                  row.estimatedMinor > 0 || row.isConverted,
                )}
                valueNote={
                  row.annualCostMinor === 0
                    ? undefined
                    : `${formatBaseMoney(row.annualCostMinor, currency)} на рік`
                }
                chevron
                onClick={() => {
                  void navigate(paymentDetailPath(row.paymentId));
                }}
              />
            ))}
          </List>
        )}

        {showsMerchants && (
          <CategoryMerchants
            merchants={merchants}
            bank={{
              marker: breakdown.colorByKey.get(BANK_SLICE_KEY),
              share: shareOf(bankMinor, breakdown.totalMinor),
              amountMinor: bankMinor,
            }}
            currency={currency}
            onOpen={(merchantKey) => {
              void navigate(summaryMerchantPath(category, merchantKey, chosen));
            }}
          />
        )}

        {hasBank && (
          <OperationList
            filter={{
              year: chosen.year,
              month: chosen.month,
              categoryId: category,
              merchantKey: null,
            }}
            baseCurrency={currency}
            onOpen={(operationId) => {
              void navigate(operationDetailPath(operationId));
            }}
          />
        )}

        <SpendingMonths
          summary={loaded}
          onChooseMonth={(month) => {
            choose(toChosenPeriod({ year: loaded.year, month }, today.year));
          }}
        />
        <WithoutForecast summary={loaded} />
      </div>
    </Screen>
  );
};
